(() => {
  const CONFIG = {
    scopeSelector: 'main, #MainContent, body', // scan broadly
    markerSelector: 'sup, span.footnote-marker',
    ignoreSelector: '.no-footnote, [data-footnote-ignore]',
    footnoteIdPrefix: 'footnote-',
    linkClass: 'footnote-ref',
    backLinkClass: 'footnote-back',
    markerClass: 'footnote-marker',
    highlightClass: 'is-highlighted',
    hideBackLinkAfterClick: true,
    getOffset() {
      const sticky =
        document.querySelector('[data-sticky-header]') ||
        document.querySelector('.section-header.is-sticky') ||
        document.querySelector('header[style*="position: sticky"]') ||
        document.querySelector('header');
      return sticky ? sticky.getBoundingClientRect().height + 8 : 0;
    },
  };

  // --- utils ---------------------------------------------------------------
  function qsa(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }
  function supValue(node) { return node.textContent.replace(/\u00A0/g, ' ').trim(); }

  // Replace a <sup> with a <span> (preserve id, classes, data-*)
  function replaceSupWithSpan(sup) {
    if (sup.tagName === 'SPAN') return sup;
    const span = document.createElement('span');
    span.className = (sup.className ? sup.className + ' ' : '') + CONFIG.markerClass;
    if (sup.id) span.id = sup.id;
    for (const attr of sup.attributes) {
      if (attr.name.startsWith('data-')) span.setAttribute(attr.name, attr.value);
    }
    sup.replaceWith(span);
    return span;
  }

  function getScrollRoot(el) {
  // Find the nearest scrollable ancestor; fall back to document.scrollingElement
    let p = el.parentElement;
    while (p) {
      const s = getComputedStyle(p);
      const canScrollY = /(auto|scroll)/.test(s.overflowY);
      if (canScrollY && p.scrollHeight > p.clientHeight) return p;
      p = p.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }

  function getOffsetTopWithin(el, scrollRoot) {
    let y = 0, n = el;
    while (n && n !== scrollRoot) { y += n.offsetTop; n = n.offsetParent; }
    return y;
  }

  function scrollToCenter(target) {
    if (!target) return;
    const headerOffset = CONFIG.getOffset();
    const root = getScrollRoot(target);
    const viewportH = (root === document.scrollingElement)
      ? window.innerHeight
      : root.clientHeight;

    const rectTop = getOffsetTopWithin(target, root);
    const y = rectTop - (viewportH / 2) + (target.offsetHeight / 2) - headerOffset;

    // smooth scroll on the correct scroller
    if (root === document.scrollingElement) {
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      root.scrollTo({ top: y, behavior: 'smooth' });
    }
  }


  function retriggerHighlight(el) {
    if (!el) return;
    el.classList.remove(CONFIG.highlightClass);
    void el.offsetWidth;
    el.classList.add(CONFIG.highlightClass);
    setTimeout(() => el.classList.remove(CONFIG.highlightClass), 3000);
  }

  function ensureBacklink(target, markerEl, refText) {
    let back = target.querySelector('.' + CONFIG.backLinkClass);
    if (!back) {
      back = document.createElement('a');
      back.className = CONFIG.backLinkClass;
      back.textContent = '↩';
      back.setAttribute('aria-label', 'Back to reference ' + refText);
      target.appendChild(document.createTextNode(' '));
      target.appendChild(back);

      back.addEventListener('click', (e) => {
        e.preventDefault();
        const refId = back.dataset.refId;
        const ref = refId ? document.getElementById(refId) : markerEl;
        if (ref) {
          scrollToCenter(ref);
          retriggerHighlight(ref);
          history.pushState(null, '', '#' + ref.id);
        }
        if (CONFIG.hideBackLinkAfterClick) {
          back.classList.add('is-fading');
          setTimeout(() => back.remove(), 180);
        }
      }, { passive: false });
    }
    back.dataset.refId = markerEl.id;
    back.href = '#' + markerEl.id;
    return back;
  }

  // === NEW: pre-normalize richtext so markers exist in the DOM =============
  function normalizeRichtextMarkers() {
    const root = document.querySelector(CONFIG.scopeSelector) || document;

    // Walk text nodes and replace [n] or literal "<sup>n</sup>" text with a marker span.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue) return NodeFilter.FILTER_REJECT;
        // skip inside scripts/styles
        const p = node.parentElement;
        if (!p || /^(script|style|noscript)$/i.test(p.tagName)) return NodeFilter.FILTER_REJECT;
        // skip if parent already a marker/link
        if (p.closest('.' + CONFIG.markerClass) || p.closest('.' + CONFIG.linkClass)) {
          return NodeFilter.FILTER_REJECT;
        }
        // look for [1] or literal "<sup>1</sup>"
        return (/\[(\d+)\]/.test(node.nodeValue) || /<sup>(\d+)<\/sup>/.test(node.nodeValue))
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      }
    });

    const toProcess = [];
    while (walker.nextNode()) toProcess.push(walker.currentNode);

    for (const textNode of toProcess) {
      const frag = document.createDocumentFragment();
      let text = textNode.nodeValue;
      const pattern = /(\[(\d+)\]|<sup>(\d+)<\/sup>)/g; // [n] OR literal "<sup>n</sup>"

      let lastIndex = 0, m;
      while ((m = pattern.exec(text)) !== null) {
        // add text before match
        if (m.index > lastIndex) frag.appendChild(document.createTextNode(text.slice(lastIndex, m.index)));
        const n = m[2] || m[3]; // group with the number
        const span = document.createElement('span');
        span.className = CONFIG.markerClass;
        span.textContent = n;
        frag.appendChild(span);
        lastIndex = m.index + m[0].length;
      }
      // trailing text
      if (lastIndex < text.length) frag.appendChild(document.createTextNode(text.slice(lastIndex)));

      textNode.replaceWith(frag);
    }
  }
  // ========================================================================

  function enhanceMarkers() {
    const root = document.querySelector(CONFIG.scopeSelector) || document;
    const candidates = qsa(CONFIG.markerSelector, root)
      .filter((el) => !el.matches(CONFIG.ignoreSelector))
      .filter((el) => !el.querySelector('a.' + CONFIG.linkClass));

    for (const node of candidates) {
      const raw = supValue(node);
      if (!/^\d+$/.test(raw)) continue; // only numeric markers like 1,2,22

      const targetId = CONFIG.footnoteIdPrefix + raw;
      const target = document.getElementById(targetId);
      if (!target) continue;

      const markerEl = replaceSupWithSpan(node);

      // ensure stable id on the marker (for backlink)
      if (!markerEl.id) {
        let base = `ref-${raw}`, id = base, i = 2;
        while (document.getElementById(id)) id = `${base}-${i++}`;
        markerEl.id = id;
      }

      // build anchor inside a <sup>
      const supWrap = document.createElement('sup');
      const a = document.createElement('a');
      a.className = CONFIG.linkClass;
      a.href = '#' + targetId;
      a.textContent = raw;
      a.setAttribute('role', 'doc-noteref');
      a.setAttribute('aria-describedby', targetId);

      supWrap.appendChild(a);

      markerEl.textContent = '';
      markerEl.appendChild(supWrap);

      a.addEventListener('click', (e) => {
        e.preventDefault();
        scrollToCenter(target);
        retriggerHighlight(target);
        history.pushState(null, '', '#' + targetId);
        ensureBacklink(target, markerEl, raw);
      }, { passive: false });
    }
  }


  // --- bootstrap -----------------------------------------------------------
  document.addEventListener('DOMContentLoaded', () => {
    const hasFootnotesSection = !!document.querySelector('.section-footnotes');
    if (!hasFootnotesSection) return;

    // NEW: normalize first, then enhance
    normalizeRichtextMarkers();
    enhanceMarkers();

    let rafQueued = false;
    const mo = new MutationObserver(() => {
      if (rafQueued) return;
      rafQueued = true;
      requestAnimationFrame(() => { 
        rafQueued = false; 
        normalizeRichtextMarkers(); // NEW on dynamic changes too
        enhanceMarkers(); 
        // center + highlight if page loads with #footnote-N
        function focusFromHash() {
          const id = (location.hash || '').slice(1);
          if (!id || !/^footnote-\d+$/.test(id)) return;
          const target = document.getElementById(id);
          if (!target) return;
          scrollToCenter(target);
          retriggerHighlight(target);
        }
        focusFromHash();
        window.addEventListener('hashchange', focusFromHash);
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  });
})();
